import React, { useMemo, useEffect, useState, useContext } from "react";
import { Stack } from "@mui/material";
import useWexClient from "../../hooks/Clients/wexClient,";
import { usePermissions } from "../../hooks/usePermissions";

import stylesService from "../../styles/Molecules/ReportesServicio/service";
import Calendario from "../../components/atoms/Calendario";
import SelectComponent from "../atoms/Select";
import { AuthContext } from "../../Auth/Context";
import { useTranslation } from "react-i18next";
import "../../Traduccion_ES_EN/i18nMiCuenta";
import AutoCompleteComponent from "../atoms/AutoCompleteComponent";

/**
 * MapsReports_ForAdmin - Vista simplificada para administradores.
 * Proporciona únicamente los filtros y selectores necesarios para monitorear el mapa.
 */
function MapsReports_ForAdmin({
  userId = "",
  endDate = null,
  setEndDate = () => {},
  startDate = null,
  setStartDate = () => {},
  selectedAgent = null,
  setSelectedAgent = () => {},
  selectedList = null,
  setSelectedList = () => {},
  setTypeReport = () => {},
  setServiceProp = () => {},
  typeReport = "",
  serviceProp = "",
  setUsers = () => {},
  users = [],
  nameAgent = [],
  setNameAgent = [],
  selectedRegionId = null,
  setSelectedRegionId = () => {},
  //PROPSESTETICOS
}) {
  const { t } = useTranslation();
  const { payload } = useContext(AuthContext);
  const [selectAgents, setSelectAgents] = useState([]);

  // Extraer valores primitivos del payload para estabilizar dependencias
  const payloadId = payload?._id;
  const payloadRegionIds = payload?.regionIds;
  const payloadRegionId = payload?.regionId;
  const payloadService = payload?.service;
  const payloadServices = payload?.services;

  const {
    locationAgents,
    usersForRegion,
    locationForAgent,
    getRegionDetail,
    getAvailableAgents,
  } = useWexClient();

  const { can } = usePermissions(payload);
  const canSelectRegion = can("cabin", "region", "*");

  const [availableRegions, setAvailableRegions] = useState([]);
  const [reportType] = useState([
    { _id: "1", name: "Real-time", value: "real-time" },
    { _id: "2", name: "Rutas", value: "route" },
    { _id: "3", name: "Check-In", value: "check-in" },
  ]);

  const [selectedOptionRep, setSelectedOptionRep] = useState("1");

  // ==================== INITIALIZE REGIONS ====================
  useEffect(() => {
    const initializeRegion = async () => {
      if (canSelectRegion && payloadId) {
        try {
          const response = await getRegionDetail(payloadId);
          const regions = (response?.data || []).map((r) => ({
            _id: r._id,
            name: r.name,
          }));
          setAvailableRegions(regions);
          // Eliminamos la pre-selección automática para que inicie limpio
        } catch (error) {
          console.error("Error fetching regions:", error);
        }
      }
    };
    initializeRegion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadId, canSelectRegion]);

  const formatDateStart = (date) => {
    if (!date) return "";
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  };

  const formatDateEnd = (date) => {
    if (!date) return "";
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  };

  const formattedStart = useMemo(() => formatDateStart(startDate), [startDate]);
  const formattedEnd = useMemo(() => formatDateEnd(endDate), [endDate]);

  const currentRegionId = useMemo(() => {
    return canSelectRegion ? selectedRegionId : payloadRegionIds;
  }, [canSelectRegion, selectedRegionId, payloadRegionIds]);

  const handleChange = (value) => setSelectedAgent(value);

  const handleChangeRep = (event) => {
    setTypeReport(event.target.value);
    setSelectedOptionRep(event.target.value);
    setSelectedAgent(null);
  };

  // ==================== AUTO-SELECT FIRST AGENT ====================
  useEffect(() => {
    if (selectAgents.length > 0 && !selectedAgent) {
      setSelectedAgent("00001");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAgents.length]);

  // ==================== FETCH AGENT OPTIONS ====================
  useEffect(() => {
    if (!["1", "2", "3"].includes(selectedOptionRep)) return;

    const fetchAgentsOptions = async () => {
      try {
        let agentsArray = [];
        const serviceId =
          payloadService || (payloadServices && payloadServices[0]);

        if (selectedOptionRep === "1") {
          const response = await getAvailableAgents({
            regionIds: currentRegionId || payloadRegionIds,
            idService: serviceId,
          });
          const exceptions = ["offline", "Conectado"];
          agentsArray = (
            Array.isArray(response?.agents) ? response.agents : []
          ).filter(
            (a) => !exceptions.includes((a.status || "").toLowerCase().trim()),
          );
        } else if (selectedOptionRep === "2") {
          const response = await usersForRegion({
            regionIds: currentRegionId || payloadRegionIds,
            serviceId,
          });
          agentsArray = (
            Array.isArray(response?.agents) ? response.agents : []
          ).filter((agent) => agent.status !== 10);
        } else if (selectedOptionRep === "3") {
          const response = await locationAgents({
            regionIds: currentRegionId || payloadRegionIds,
            serviceId,
          });
          agentsArray = Array.isArray(response?.agents) ? response.agents : [];
        }

        if (!Array.isArray(agentsArray)) return;
        const simplifiedAgents = agentsArray
          .map((item) => {
            if (selectedOptionRep === "1")
              return {
                _id: item.agent?._id || item.agent?.user,
                name: item.agent?.nameUser || item.agent?.user,
                locationDt: item.agent?.locationDt,
              };
            if (selectedOptionRep === "2")
              return { _id: item._id, name: item.user };
            if (selectedOptionRep === "3")
              return { _id: item.user_id, name: item.user };
            return null;
          })
          .filter(Boolean);

        const initialOption = { _id: "00001", name: "Buscar agente..." };
        const fullList = [initialOption, ...simplifiedAgents];
        const nameAgents = simplifiedAgents.map((agent) => ({
          _id: agent._id,
          name: agent.name,
          locationDt: agent.locationDt,
        }));

        setUsers([]);
        setNameAgent(nameAgents);
        setSelectAgents(fullList);
        setSelectedAgent(initialOption._id);
      } catch (error) {
        console.error("Error fetching agent options:", error);
      }
    };
    fetchAgentsOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptionRep, currentRegionId, payloadService, payloadServices]);

  // ==================== FETCH SPECIFIC AGENT DATA FOR MAP ====================
  useEffect(() => {
    const fetchAgent = async () => {
      if (!selectedAgent || selectedAgent === "00001") {
        setUsers([]);
        return;
      }
      try {
        const serviceId =
          payloadService || (payloadServices && payloadServices[0]);
        if (selectedOptionRep === "1") {
          const response = await getAvailableAgents({
            regionIds: currentRegionId || payloadRegionIds,
            idService: serviceId,
          });
          const exeptions = new Set(["offline", "conectado"]);
          const agentsArray = (response?.agents || []).filter(
            (a) => !exeptions.has((a.status || "").toLowerCase().trim()),
          );
          setUsers(
            agentsArray.map((agent) => ({
              id: agent.agent?._id,
              usuario: agent.agent?.user,
              name: agent.agent?.nameUser || agent.agent?.user,
              locationDt: agent.agent?.locationDt,
              status: agent.status,
              location: agent.agent.location,
            })),
          );
        } else if (selectedOptionRep === "2" || selectedOptionRep === "3") {
          const body = {
            regionId: currentRegionId,
            idAgent: selectedAgent,
            type: selectedOptionRep === "2" ? "route" : "check-in",
            startDate: formattedStart,
            endDate: formattedEnd,
            serviceId,
          };
          console.log("body para rutas/checkin", body);
          const response = await locationForAgent(body);

          const agentsArray = response?.agents || response?.data || [];
          setUsers(
            agentsArray
              .map((agent) => ({
                id: agent.user_id?._id || agent.user_id,
                usuario: agent.user_id?.user || agent.user || "Desconocido",
                name:
                  agent.user_id?.profile?.name ||
                  agent.user_id?.user ||
                  agent.nameUser ||
                  agent.user ||
                  "Desconocido",
                location: agent.location,
                date: agent.dateCheckIn || agent.locationAt || agent.createdAt,
              }))
              .filter((a) => a.location),
          );
        }
      } catch (error) {
        console.log("error", error.message);
        setUsers([]);
      }
    };
    fetchAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOptionRep,
    selectedAgent,
    formattedStart,
    formattedEnd,
    currentRegionId,
    payloadService,
    payloadServices,
  ]);

  return (
    <Stack>
      <Stack mb={4} sx={stylesService.stack}>
        {canSelectRegion && availableRegions.length > 0 && (
          <SelectComponent
            onChange={(e) => setSelectedRegionId(e.target.value)}
            value={selectedRegionId || ""}
            valueSelect="_id"
            options={availableRegions}
            colorLabel="white"
            fontSizeInput="15px"
            label="Selecciona la Región"
            textColor="gray"
            backgroundColor="white"
          />
        )}

        <SelectComponent
          onChange={handleChangeRep}
          value={selectedOptionRep || ""}
          valueSelect="_id"
          options={reportType}
          colorLabel="white"
          fontSizeInput="15px"
          label={t("Welcome.text65")}
          textColor="gray"
          backgroundColor="white"
        />
        <AutoCompleteComponent
          onChange={handleChange}
          value={selectedAgent || ""}
          valueKey="_id"
          options={selectAgents}
          label={t("Welcome.text273")}
          colorLabel="white"
          fontSizeInput="15px"
          textColor="gray"
          backgroundColor="white"
        />
      </Stack>

      <Stack sx={{ width: "100%" }} spacing={4}>
        {(selectedOptionRep === "2" || selectedOptionRep === "3") && (
          <Stack sx={{ ...stylesService.stack, width: "70%" }}>
            <Calendario
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default MapsReports_ForAdmin;
